import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ShipmentProductList } from '@/apis/types';
import {
  editLinesFromApi,
  isImporterManaged,
  productLinesPayload,
  type EditProductLine,
  type ProductLinePayload,
} from './editProductLines';
import {
  shipmentProductLinesKey,
  useShipmentProductLinesQuery,
} from './editShipmentQueries';

const NO_ROWS: ShipmentProductList[] = [];

/** The edit drawer's product lines: loaded fresh for each shipment opened, edited locally, and turned into a `products` payload only when changed (spec UE3, UE4). */
export function useEditProductLines(
  shipmentId: string,
  enabled: boolean,
  fromPieces: boolean
) {
  const queryClient = useQueryClient();
  const query = useShipmentProductLinesQuery(shipmentId, enabled);
  const rows = query.data ?? NO_ROWS;
  const initialLines = editLinesFromApi(rows);
  const readOnly = fromPieces || isImporterManaged(rows);
  const [editedLines, setEditedLines] = useState<EditProductLine[] | null>(
    null
  );
  const [session, setSession] = useState(shipmentId);
  if (session !== shipmentId) {
    setSession(shipmentId);
    setEditedLines(null);
  }
  const ready = query.isSuccess && !query.isFetching;
  const lines = editedLines ?? initialLines;

  const setLines: Dispatch<SetStateAction<EditProductLine[]>> = (action) => {
    if (!ready) return;
    setEditedLines((prev) =>
      typeof action === 'function' ? action(prev ?? initialLines) : action
    );
  };

  const payload = (): ProductLinePayload[] | undefined =>
    ready && !readOnly && editedLines !== null
      ? productLinesPayload(initialLines, editedLines)
      : undefined;

  return {
    rows,
    lines,
    setLines,
    payload,
    readOnly,
    ready,
    isLoading: enabled && (query.isPending || query.isFetching),
    isError: query.isError && !query.isFetching,
    retry: () => {
      const _ = query.refetch();
    },
    afterSave: () => {
      setEditedLines(null);
      const _ = queryClient.invalidateQueries({
        queryKey: shipmentProductLinesKey(shipmentId),
      });
    },
  };
}
